import * as THREE from 'three'
import { gsap } from 'gsap'
import Experience from '../../Experience'
import bodyVertexShader from './Shader/body/vertex.glsl'
import bodyFragmentShader from './Shader/body/fragment.glsl'
import haloVertexShader from './Shader/halo/vertex.glsl'
import haloFragmentShader from './Shader/halo/fragment.glsl'
import ambientVertexShader from './Shader/ambientParticles/vertex.glsl'
import ambientFragmentShader from './Shader/ambientParticles/fragment.glsl'
import ringsVertexShader from './Shader/rings/vertex.glsl'
import ringsFragmentShader from './Shader/rings/fragment.glsl'

// config.surfaceStyle → body シェーダーの SURFACE_STYLE define（未指定は従来の bands）
const SURFACE_STYLES = { bands: 0, saturn: 1, sun: 2, pearl: 3, auroraIce: 4 }

/**
 * CelestialBody — 周回する天体1つ分。
 *
 * 階層構造で軌道を表現する（ワールド座標の三角関数計算を毎フレームしない）:
 *   orbitGroup (rotation.y = node, rotation.x = inclination で軌道面を決める)
 *     └─ pivot (rotation.y = θ で公転)
 *          └─ bodyGroup at (orbitRadius, 0, 0)  … 球体 + ハロービルボード
 *
 * マテリアルはすべて自己発光の ShaderMaterial なので、
 * Environment の消灯演出（暗いイントロ）を壊さない。
 */
export default class CelestialBody
{
  constructor(config)
  {
    this.experience = new Experience()
    this.scene = this.experience.scene
    this.quality = this.experience.quality

    this.config = config
    this.name = config.name
    this.inZone = false // トリガーゾーン内かどうか（マネージャが管理）

    // 疑似太陽の方向（Environment.sunLight の位置と揃える）
    this.lightDir = new THREE.Vector3(3.5, 2, -1.25).normalize()
    this.seed = Math.random()
    this.scratchQuaternion = new THREE.Quaternion()

    this.setHierarchy()
    this.setBody()
    this.setHalo()

    // config に指定があれば、傾いたリング（土星風）を付ける
    if (this.config.rings) {
      this.setRings()
    }

    // config に指定があれば、天体のまわりに常時漂う淡い粒を付ける
    if (this.config.ambientParticles) {
      this.setAmbientParticles()
    }

    this.scene.add(this.orbitGroup)
  }

  setHierarchy()
  {
    this.orbitGroup = new THREE.Group()
    this.orbitGroup.rotation.order = 'YXZ'
    this.orbitGroup.rotation.y = this.config.node
    this.orbitGroup.rotation.x = this.config.inclination

    this.pivot = new THREE.Group()
    this.orbitGroup.add(this.pivot)

    this.bodyGroup = new THREE.Group()
    this.bodyGroup.position.set(this.config.orbitRadius, 0, 0)
    this.pivot.add(this.bodyGroup)
  }

  setBody()
  {
    const segments = this.quality.tier === 'low' ? 24 : 48
    const geometry = new THREE.SphereGeometry(this.config.bodyRadius, segments, segments)

    this.bodyMaterial = new THREE.ShaderMaterial({
      vertexShader: bodyVertexShader,
      fragmentShader: bodyFragmentShader,
      defines: {
        SURFACE_STYLE: SURFACE_STYLES[this.config.surfaceStyle] ?? SURFACE_STYLES.bands,
        FBM_OCTAVES: this.quality.tier === 'low' ? 3 : 4,
      },
      uniforms: {
        uColorA: new THREE.Uniform(new THREE.Color(this.config.colorA)),
        uColorB: new THREE.Uniform(new THREE.Color(this.config.colorB)),
        uRimColor: new THREE.Uniform(new THREE.Color(this.config.haloColor)),
        uLightDir: new THREE.Uniform(this.lightDir),
        uTime: new THREE.Uniform(0),
        uAlpha: new THREE.Uniform(0), // イントロで GSAP フェードイン
        uBandFreq: new THREE.Uniform(this.config.bandFreq),
        uSeed: new THREE.Uniform(this.seed),
      },
      transparent: true,
    })

    this.bodyMesh = new THREE.Mesh(geometry, this.bodyMaterial)
    this.bodyGroup.add(this.bodyMesh)
  }

  setHalo()
  {
    const geometry = new THREE.PlaneGeometry(1, 1)

    this.haloMaterial = new THREE.ShaderMaterial({
      vertexShader: haloVertexShader,
      fragmentShader: haloFragmentShader,
      uniforms: {
        uColor: new THREE.Uniform(new THREE.Color(this.config.haloColor)),
        uIntensity: new THREE.Uniform(0), // uAlpha と一緒にフェードイン
        uTime: new THREE.Uniform(0),
        uSeed: new THREE.Uniform(this.seed),
      },
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    })

    this.haloMesh = new THREE.Mesh(geometry, this.haloMaterial)
    this.haloMesh.scale.setScalar(this.config.haloScale)
    this.haloMesh.renderOrder = 1 // 星空(-1)や天体本体より後に加算描画
    this.bodyGroup.add(this.haloMesh)
  }

  // 傾いたリング（RingGeometry は XY 平面・法線 +Z なので、水平に倒してから傾ける）
  setRings()
  {
    const { innerScale, outerScale, tilt, color } = this.config.rings
    const innerRadius = this.config.bodyRadius * innerScale
    const outerRadius = this.config.bodyRadius * outerScale

    this.ringGroup = new THREE.Group()
    this.ringGroup.rotation.order = 'XYZ'
    this.ringGroup.rotation.x = -Math.PI / 2 + tilt // 水平面に倒して傾きを付ける
    this.ringGroup.rotation.z = 0.2                 // 少し斜めに構える
    this.bodyGroup.add(this.ringGroup)

    const geometry = new THREE.RingGeometry(innerRadius, outerRadius, 96, 4)

    this.ringMaterial = new THREE.ShaderMaterial({
      vertexShader: ringsVertexShader,
      fragmentShader: ringsFragmentShader,
      uniforms: {
        uColor: new THREE.Uniform(new THREE.Color(color)),
        uInnerRadius: new THREE.Uniform(innerRadius),
        uOuterRadius: new THREE.Uniform(outerRadius),
        uAlpha: new THREE.Uniform(0), // 本体と一緒にフェードイン
        uGlow: new THREE.Uniform(0),  // ゾーン発動中の増光
        uTime: new THREE.Uniform(0),
      },
      transparent: true,
      side: THREE.DoubleSide,
      depthWrite: false,
    })

    this.ringMesh = new THREE.Mesh(geometry, this.ringMaterial)
    this.ringMesh.renderOrder = 1
    this.ringGroup.add(this.ringMesh)
  }

  // 天体のまわりに漂う淡い粒（bodyGroup の子なので公転に追従する）
  setAmbientParticles()
  {
    const { count, radius, color, size, layout } = this.config.ambientParticles
    const sizes = this.experience.sizes

    const positions = new Float32Array(count * 3)
    const scales = new Float32Array(count)
    const offsets = new Float32Array(count)

    for (let i = 0; i < count; i++) {
      if (layout === 'ring' && this.config.rings) {
        // リング面（ringGroup ローカルの XY 平面）に沿った円環に散らす
        const angle = Math.random() * Math.PI * 2
        const inner = this.config.bodyRadius * this.config.rings.innerScale
        const outer = this.config.bodyRadius * this.config.rings.outerScale
        const distance = inner + Math.random() * (outer - inner)

        positions[i * 3 + 0] = Math.cos(angle) * distance
        positions[i * 3 + 1] = Math.sin(angle) * distance
        positions[i * 3 + 2] = (Math.random() - 0.5) * 0.25 // リング面の厚みぶんの揺らぎ
      } else {
        // 天体を包む球殻にランダム配置
        const innerRadius = this.config.bodyRadius * 1.4
        const direction = new THREE.Vector3(
          Math.random() - 0.5, Math.random() - 0.5, Math.random() - 0.5
        ).normalize()
        const distance = innerRadius + Math.random() * (radius - innerRadius)

        positions[i * 3 + 0] = direction.x * distance
        positions[i * 3 + 1] = direction.y * distance
        positions[i * 3 + 2] = direction.z * distance
      }

      scales[i] = 0.5 + Math.random()
      offsets[i] = Math.random()
    }

    const geometry = new THREE.BufferGeometry()
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3))
    geometry.setAttribute('aScale', new THREE.BufferAttribute(scales, 1))
    geometry.setAttribute('aOffset', new THREE.BufferAttribute(offsets, 1))

    this.ambientMaterial = new THREE.ShaderMaterial({
      vertexShader: ambientVertexShader,
      fragmentShader: ambientFragmentShader,
      uniforms: {
        uTime: new THREE.Uniform(0),
        uSize: new THREE.Uniform(size),
        uPixelRatio: new THREE.Uniform(Math.min(sizes.pixelRatio, this.quality.maxPixelRatio)),
        uAlpha: new THREE.Uniform(0), // 本体の uAlpha と一緒にフェードイン
        uColor: new THREE.Uniform(new THREE.Color(color)),
      },
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    })

    this.ambientParticles = new THREE.Points(geometry, this.ambientMaterial)
    this.ambientParticles.renderOrder = 1 // ハローと同じ層で加算描画
    // ring レイアウトはリングの傾きに追従させる
    const parent = (layout === 'ring' && this.ringGroup) ? this.ringGroup : this.bodyGroup
    parent.add(this.ambientParticles)
  }

  // 公転角をセット（マネージャの update から呼ばれる）
  setAngle(theta)
  {
    this.pivot.rotation.y = theta
  }

  // エフェクト発動中はハローを最大サイズまで広げる（元のサイズは覚えておく）
  expandHalo(maxScale)
  {
    this.haloBaseScale = this.haloMesh.scale.x
    gsap.to(this.haloMesh.scale, {
      x: maxScale, y: maxScale, z: maxScale,
      duration: Math.max(2.5 * this.quality.motionScale, 0.01),
      ease: 'power2.inOut',
      overwrite: true,
    })
  }

  // エフェクト解除時に発動前のサイズへ戻す（debug GUI での調整値を尊重）
  restoreHalo()
  {
    const baseScale = this.haloBaseScale ?? this.config.haloScale
    gsap.to(this.haloMesh.scale, {
      x: baseScale, y: baseScale, z: baseScale,
      duration: Math.max(2.5 * this.quality.motionScale, 0.01),
      ease: 'power2.inOut',
      overwrite: true,
    })
  }

  // トリガー判定用のワールド座標（target は使い回しのスクラッチベクトル）
  getWorldPosition(target)
  {
    return this.bodyGroup.getWorldPosition(target)
  }

  update(elapsed, cameraQuaternion)
  {
    this.bodyMaterial.uniforms.uTime.value = elapsed
    this.haloMaterial.uniforms.uTime.value = elapsed

    if (this.ambientMaterial) {
      this.ambientMaterial.uniforms.uTime.value = elapsed
    }

    if (this.ringMaterial) {
      this.ringMaterial.uniforms.uTime.value = elapsed
    }

    // ハローは常にカメラに正対させる（親の回転を打ち消してワールドでカメラ向きにする）
    this.bodyGroup.getWorldQuaternion(this.scratchQuaternion)
    this.haloMesh.quaternion.copy(this.scratchQuaternion.invert()).multiply(cameraQuaternion)
  }

  dispose()
  {
    this.scene.remove(this.orbitGroup)
    this.bodyMesh.geometry.dispose()
    this.bodyMaterial.dispose()
    this.haloMesh.geometry.dispose()
    this.haloMaterial.dispose()

    if (this.ambientParticles) {
      this.ambientParticles.geometry.dispose()
      this.ambientMaterial.dispose()
    }

    if (this.ringMesh) {
      this.ringMesh.geometry.dispose()
      this.ringMaterial.dispose()
    }
  }
}
